import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import FormSectionLayout from "components/layouts/FormSectionLayout";
import HeadingText from "components/common/HeadingText/HeadingText";
import {
  Col,
  Container,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Spinner,
} from "reactstrap";
import ButtonComponent from "components/common/Button/ButtonComponent";
import LoaderComponent from "components/common/Loader/Loader";
import TextField from "components/common/TextField/TextField";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import Switch from "components/common/Switch/Switch";
import UploadButtonComponent from "components/common/UploadButton/UploadButtonComponent";
import SortableList from "components/common/SortableList/SortableList";
import Modal from "components/common/Modal/Modal";
import DataTable from "components/common/DataTable/DataTable";
import CloseSvg from "assets/images/close.svg";
import SearchIcon from "assets/images/search-icon.svg";
import ProductPlaceHolder from "assets/product_placeholder_image.png";
import { IImageObject, IOrderAddUpdateList } from "interfaces/Index";
import { _ } from "utils/lib";
import {
  normalizeBannerImagePayload,
  normalizeUpdateBannerPayload,
  normalizeUpdateBannerProductsPayload,
} from "normalizer/BannerNormalizer";
import { AbilityContext, Can } from "config/Can";
import { IBannerPageState } from "interfaces/banner.interface";
import { IBannerDetailPageProps } from "./BannerDetailPage.interface";
import { imageUploadKeys } from "./BannerDetailPage.types";
import {
  BANNER_IMAGE_DIMENSIONS,
  bodyDataAndKeyTypes,
  footerData,
} from "./constants/BannerDetails.constant";

import "./BannersDetailPage.scss";
import { BANNER_TYPES } from "./constants/BannerDetails.constant";
import { doProductAPIsearch } from "./helpers/BannerDetailPage.helper";
import { acceptableFormats, STATUS } from "config/constants";
import BackButtonComponent from "components/common/BackButton/BackButtonComponent";

const BannersDetailPage: React.FunctionComponent<IBannerDetailPageProps> = (
  props
) => {
  const ability = useContext(AbilityContext);
  let selectedProducts: any = useRef([]);
  let selectedProductIndex: any = useRef([]);

  const {
    selectedCity,
    productDataProp,
    selectedBanner,
    isBannerAndProductsUpdated,
    isError,
    error,
    backButtonHandler,
    updateBanner,
    updateBannerWithProducts,
    uploadImageAndUpdateBannerWithProducts,
    uploadBannerImageAction,
    selectedCustomerChannel,
  } = props;
  const headingText = "Banners";

  const [bannerState, setBannerState] = useState<IBannerPageState>({
    id: selectedBanner.id || "",
    cityId: selectedCity?.id || "",
    customerChannel: selectedCustomerChannel?.value || "",
    bannerDescription: selectedBanner.bannerDescription || "",
    bannerTitle: selectedBanner.bannerTitle || "",
    status: selectedBanner.status === STATUS.ACTIVE,
    bannerType: selectedBanner.type || "",
    w400Xh400: {
      base64StringUrl: selectedBanner.w400Xh400.base64StringUrl || "",
      imageId: selectedBanner.w400Xh400.imageId || "",
      multiPartData: selectedBanner.w400Xh400.multiPartData || "",
    },
    w2000Xh375: {
      base64StringUrl: selectedBanner.w2000Xh375.base64StringUrl || "",
      imageId: selectedBanner.w2000Xh375.imageId || "",
      multiPartData: selectedBanner.w2000Xh375.multiPartData || "",
    },
    productSequence: {
      products: selectedBanner.products.slice() || [],
    },
  });

  const [isSearch, setIsSearching] = useState(props.isSearch || false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setIsShowModal] = useState(false);
  const [productData, setProductData] = useState<Array<IOrderAddUpdateList>>(
    productDataProp || []
  );
  const [isProductLoading, setProductLoading] = useState(false);

  useEffect(() => {
    if (isBannerAndProductsUpdated) {
      backButtonHandler(true);
    }
  }, [isBannerAndProductsUpdated]);

  useEffect(() => {
    if (isError) {
      alert((error! as any).errors);
    }
  }, [isError]);

  const productSequenceValidate =
    bannerState.bannerType === BANNER_TYPES.PRODUCT
      ? bannerState.productSequence.products.length > 0
      : true;

  const validateForm = !!(
    bannerState.bannerTitle &&
    bannerState.bannerDescription.length > 0 &&
    bannerState.w400Xh400 &&
    bannerState.w2000Xh375 &&
    productSequenceValidate
  );

  const closeModal = () => {
    selectedProducts.current = [];
    selectedProductIndex.current = [];
    setProductData([]);
    setIsShowModal(false);
    setIsSearching(false);
    setSearchTerm("");
  };
  const closeSearch = () => {
    setIsSearching(false);
    setSearchTerm("");
    setProductData([]);
  };

  const handleChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    setBannerState({
      ...bannerState,
      [fieldId]: fieldId === "status" ? !bannerState[fieldId] : fieldValue,
    });
  };

  const handleBannerUpload = (payload: any, fieldName: imageUploadKeys) => {
    setBannerState({
      ...bannerState,
      [fieldName]: { ...bannerState[fieldName], ...payload },
    });
  };

  const handleBannerProductRemove = (index: number) => {
    bannerState.productSequence.products.splice(index, 1);
    updateProductSequence(bannerState.productSequence.products);
  };

  const updateProductSequence = (bannerProducts: any) => {
    setBannerState({
      ...bannerState,
      productSequence: {
        wasModified: true,
        products: bannerProducts,
      },
    });
  };

  const handleProductSearchInputChange = (e: any) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 3) {
      debounceSearch(
        selectedCity?.id || "",
        value,
        bannerState.productSequence.products
      );
    }
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      setProductData([]);
    }
  };

  const debounceSearch = useCallback(
    _.debounce((cityId: string, value: any, productSequenceProducts: any) => {
      doProductAPIsearch(
        cityId,
        value,
        setProductData,
        setProductLoading,
        selectedProducts.current,
        productSequenceProducts
      );
    }, 1000),
    []
  );

  const handleBackButton = () => {
    backButtonHandler(false);
  };

  const handleRecordSelection = (event: any, selectedIndex: any) => {
    if (productData[selectedIndex].isChecked) {
      // remove from selected Items
      const index = selectedProductIndex.current.indexOf(selectedIndex);
      selectedProductIndex.current.splice(index, 1);
      selectedProducts.current.splice(index, 1);
      productData[selectedIndex].isChecked = false;
    } else {
      selectedProductIndex.current.push(selectedIndex);
      selectedProducts.current.push(productData[selectedIndex]);
      productData[selectedIndex].isChecked = true;
    }

    setProductData([...productData]);
  };

  const updateBannerProductList = () => {
    if (selectedProducts.current.length) {
      updateProductSequence([
        ...bannerState.productSequence.products,
        ...selectedProducts.current,
      ]);
    }
    selectedProducts.current = [];
    selectedProductIndex.current = [];
    setIsShowModal(false);
    setIsSearching(false);
    setSearchTerm("");
    setProductData([]);
  };

  const handleSubmit = () => {
    const { w400Xh400, productSequence, w2000Xh375 } = bannerState;

    const bannerPayload = normalizeUpdateBannerPayload(bannerState);
    const bannerProductsPayload = normalizeUpdateBannerProductsPayload(
      productSequence.products
    );

    if (
      (w400Xh400 as IImageObject).multiPartData ||
      (w2000Xh375 as IImageObject).multiPartData
    ) {
      const imgFormData: FormData = normalizeBannerImagePayload(
        bannerState,
        BANNER_IMAGE_DIMENSIONS.W400Xh400
      );

      const desktopImageFormData: FormData = normalizeBannerImagePayload(
        bannerState,
        BANNER_IMAGE_DIMENSIONS.W2000Xh375
      );

      bannerState.bannerType === BANNER_TYPES.PRODUCT ||
      bannerState.bannerType === BANNER_TYPES.PROMO
        ? uploadImageAndUpdateBannerWithProducts(
            imgFormData,
            desktopImageFormData,
            bannerPayload,
            bannerProductsPayload
          )
        : uploadBannerImageAction(imgFormData, desktopImageFormData);
      return;
    } else if (productSequence.wasModified) {
      updateBannerWithProducts(bannerPayload, bannerProductsPayload);
      return;
    }

    updateBanner(bannerPayload);
  };

  const renderProductList = () => {
    return (
      <Modal
        showModal={showModal}
        modalHeading="Product search"
        closeModalHandler={closeModal}
        showProgressString={false}
        failedRecords=""
        modalBodyClass="product-search"
      >
        {renderSearchProductModalBody()}
      </Modal>
    );
  };

  const renderSearchProductModalBody = () => {
    const buttonText =
      selectedProducts.current.length > 0
        ? `Add products (${selectedProducts.current.length} items selected)`
        : `Add products`;
    return (
      <>
        <InputGroup>
          <InputGroupAddon addonType="prepend">
            <InputGroupText>
              <img src={SearchIcon} alt="search" />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            className="search-input"
            placeholder="Search by product name"
            onChange={handleProductSearchInputChange}
            value={searchTerm}
            autoFocus={true}
          />
          <div
            className={isSearch ? "close-button" : "hide close-button"}
            onClick={closeSearch}
          >
            <img src={CloseSvg} alt="close" />
          </div>
        </InputGroup>
        <section className="product-list-container">
          {productData.length > 0 && (
            <DataTable
              tableId="banner-product-list"
              tableHeaderData={null}
              tableFooterData={footerData}
              tableBodyDataTypes={bodyDataAndKeyTypes}
              tableBodyRowData={productData}
              noSearchResultFlag={isSearch}
              editRowHandler={handleRecordSelection}
            />
          )}
          {isProductLoading && (
            <div>
              <Spinner color="primary" className="spinner" />
            </div>
          )}
        </section>
        <div className="update-banner-products-container">
          <ButtonComponent
            buttonClass="large-button btn-primary"
            buttonText={buttonText}
            onClickHandler={updateBannerProductList}
          />
        </div>
      </>
    );
  };

  const renderBannerDetail = () => {
    return (
      <FormSectionLayout subTitleText="Banner details">
        <SearchableSelect
          id="selectedCity"
          isSearchable={true}
          options={[]}
          selectedValue={selectedCity}
          // onChange={handleSelectedCityChange}
          label="City"
          isDisabled
        />
        <TextField
          id="bannerType"
          label="Type"
          placeholder="Type"
          value={bannerState.bannerType}
          disabled
        />
        <TextField
          id="bannerTitle"
          label="Title"
          placeholder="Enter Banner Title"
          value={bannerState.bannerTitle}
          handleChange={handleChange}
          isOptional={false}
        />
        <Switch
          id="status"
          label="Status"
          checked={bannerState.status as boolean}
          handleSwitch={handleChange}
          statusVal1={"ACTIVE"}
          statusVal2={"INACTIVE"}
        />
      </FormSectionLayout>
    );
  };

  const renderBannerImagePicker = () => {
    const { w400Xh400 } = bannerState;
    return (
      <FormSectionLayout subTitleText="Mobile Banner Image">
        <div className="banner-image-upload-container">
          {w400Xh400 && (w400Xh400! as any).base64StringUrl ? (
            <div className="banner-image-container">
              <img src={(w400Xh400! as any).base64StringUrl} alt="product" />
            </div>
          ) : (
            <img src={ProductPlaceHolder} alt="placeholder" />
          )}
          <Can I="update" a="banner">
            <UploadButtonComponent
              id="bannerImage"
              helperText="740x320 - max 120KB"
              buttonText="Upload .webp image"
              allowedFileSize={120}
              allowedDimensions={{
                width: 740,
                height: 320,
              }}
              fileFormat={acceptableFormats.image}
              handleFileChange={(event) =>
                handleBannerUpload(event, "w400Xh400")
              }
            />
          </Can>
        </div>
      </FormSectionLayout>
    );
  };

  const renderDesktopBannerImagePicker = () => {
    const { w2000Xh375 } = bannerState;
    return (
      <FormSectionLayout subTitleText="Desktop Banner Image">
        <div className="banner-image-upload-container">
          {w2000Xh375 && (w2000Xh375! as any).base64StringUrl ? (
            <div className="banner-image-container">
              <img src={(w2000Xh375! as any).base64StringUrl} alt="product" />
            </div>
          ) : (
            <img src={ProductPlaceHolder} alt="placeholder" />
          )}
          <Can I="update" a="banner">
            <UploadButtonComponent
              id="w2000Xh375"
              helperText="1440x270 - max 120KB"
              buttonText="Upload .webp image"
              fileFormat={acceptableFormats.image}
              allowedFileSize={120}
              allowedDimensions={{
                width: 1440,
                height: 270,
              }}
              handleFileChange={(event) =>
                handleBannerUpload(event, "w2000Xh375")
              }
            />
          </Can>
        </div>
      </FormSectionLayout>
    );
  };

  const renderBannerProductList = () => {
    const { productSequence, bannerType } = bannerState;
    const isHomeBanner = bannerType === BANNER_TYPES.PRODUCT;
    return (
      isHomeBanner && (
        <FormSectionLayout subTitleText="Product sequence">
          {renderProductSearchInput()}
          {productSequence.products.length > 0 && (
            <div className="product-sequence-container">
              <SortableList
                categoryProducts={productSequence.products}
                handleProductSortEnd={updateProductSequence}
                isCross
                crossHandler={handleBannerProductRemove}
                distance={1}
              />
            </div>
          )}
        </FormSectionLayout>
      )
    );
  };

  const renderFormButton = () => {
    return (
      <FormSectionLayout subTitleText="">
        <div className="form-row-separator">
          <Row id="addEditButtonRow" className="add-update-button-container">
            <Col lg="6">
              {ability.can("update", "banner") && (
                <ButtonComponent
                  buttonClass="large-button btn-primary"
                  buttonText={bannerState.id ? "Update banner" : "Add banner"}
                  onClickHandler={handleSubmit}
                  isDisable={props.isLoading || !validateForm}
                />
              )}
            </Col>
          </Row>
        </div>
      </FormSectionLayout>
    );
  };

  const renderProductSearchInput = () => {
    return (
      <section className="search-container">
        <InputGroup>
          <InputGroupAddon addonType="prepend">
            <InputGroupText className={""}>
              <img src={SearchIcon} alt="search" />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            className="search-input"
            placeholder="Search by product name"
            onFocus={() => setIsShowModal(true)}
          />
        </InputGroup>
      </section>
    );
  };

  return (
    <Container fluid className="banner-module">
      {renderProductList()}
      {props.isLoading && <LoaderComponent />}
      <Row>
        <Col lg="5">
          <BackButtonComponent
            backButtonText="Back to Banners"
            backButtonHandler={handleBackButton}
          />
          <HeadingText text={headingText} isMainHeading={true} />
        </Col>
      </Row>
      <Row className="form-container">
        <Col xs="12">
          <Form>
            <div className="banner-section">{renderBannerDetail()}</div>
            <div className="banner-section">{renderBannerImagePicker()}</div>
            <div className="banner-section">
              {renderDesktopBannerImagePicker()}
            </div>
            <div className="banner-section banner-product-search-container">
              {renderBannerProductList()}
            </div>
            {renderFormButton()}
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default BannersDetailPage;
